import { useMemo } from "react";

import { CELL_TYPES, getCellType, getValueParser } from "./Cells/Dynamic";
import { isNonEmptyString, isTrimmedStringNonEmpty } from "./Utils/string";
import { COLUMN_TYPES, SORT_DIRECTIONS } from "./const";

export default function useSortedRows(
  rows,
  { columnID, columnType, direction },
  forceRefresh
) {
  return useMemo(() => {
    if (!(columnID && columnType && direction)) return rows;

    const newRows = [...rows];

    // we are calculating the diff via { (a, b) => a - b }, NOT { (a, b) => b - a }
    const directionMultiplier =
      direction === SORT_DIRECTIONS.ASCENDING ? 1 : -1;

    const dataColumnID = `data_${columnID}`;
    const cellType = getCellType(columnType);
    const parser = getValueParser(columnType);

    // TODO definitely an opportunity for some simplification here
    switch (cellType) {
      case CELL_TYPES.BOOL:
        return newRows.sort((a, b) => {
          const dataA = parseInt(a[dataColumnID]) || 0;
          const dataB = parseInt(b[dataColumnID]) || 0;

          const diff =
            dataA === dataB ? a.originalIndex - b.originalIndex : dataA - dataB;

          return diff * directionMultiplier;
        });
      case CELL_TYPES.TEXT:
        return newRows.sort((a, b) => {
          const dataA =
            isTrimmedStringNonEmpty(a[dataColumnID]) &&
            a[dataColumnID].trim().toLowerCase();
          const dataB =
            isTrimmedStringNonEmpty(b[dataColumnID]) &&
            b[dataColumnID].trim().toLowerCase();

          //make sure empty rows are put at the end regardless of sorting order
          if (dataA === dataB)
            return (a.originalIndex - b.originalIndex) * directionMultiplier;

          if (!dataA) return 1;
          if (!dataB) return -1;

          return dataA.localeCompare(dataB) * directionMultiplier;
        });
      case CELL_TYPES.NUMBER:
        return newRows.sort((a, b) => {
          const dataA =
            isNonEmptyString(a[dataColumnID]) && parseFloat(a[dataColumnID]);
          const dataB =
            isNonEmptyString(b[dataColumnID]) && parseFloat(b[dataColumnID]);

          //make sure empty rows are put at the end regardless of sorting order
          if (dataA === dataB)
            return (a.originalIndex - b.originalIndex) * directionMultiplier;

          if (!dataA) return 1;
          if (!dataB) return -1;

          return (dataA - dataB) * directionMultiplier;
        });
      case CELL_TYPES.DATE:
      case CELL_TYPES.DATE_TIME:
        return newRows.sort((a, b) => {
          const rawA = a[dataColumnID];
          const rawB = b[dataColumnID];

          /**
           * This is a hacky way to disrupt the encapsulated parsing
           *   in order to handle columns with a mixture of Date Cells that
           *   both DO and DO NOT account for the Year
           * We always want the group of Dates WITH years to appear before
           *   the group of those without dates
           */
          let isADateWithoutYear,
            isBDateWithoutYear = false;
          const shouldHandleDatesWithoutYears =
            columnType === COLUMN_TYPES.EXTENDED_FIELDS_DATE;
          if (shouldHandleDatesWithoutYears) {
            try {
              if (rawA) {
                const { year: yearA } = JSON.parse(rawA);
                isADateWithoutYear = typeof yearA !== "number";
              }
              if (rawB) {
                const { year: yearB } = JSON.parse(rawB);
                isBDateWithoutYear = typeof yearB !== "number";
              }
            } catch (e) {
              console.error(e);
            }
          }

          const parsedA = parser(rawA);
          const parsedB = parser(rawB);
          const dataA = isNonEmptyString(parsedA) && new Date(parsedA);
          const dataB = isNonEmptyString(parsedB) && new Date(parsedB);

          /**
           * `&& isADateWithoutYear !== isBDateWithoutYear` to handle the edge case
           *   where one of the equivalent dates does not account for the year,
           *   and the other date is in the year 2001 - thanks JavaScript :|
           */
          if (dataA === dataB && isADateWithoutYear !== isBDateWithoutYear)
            return (a.originalIndex - b.originalIndex) * directionMultiplier;

          if (!dataA) return 1;
          if (!dataB) return -1;

          if (shouldHandleDatesWithoutYears) {
            if (isADateWithoutYear && !isBDateWithoutYear) return 1;
            if (!isADateWithoutYear && isBDateWithoutYear) return -1;
          }

          return (dataA - dataB) * directionMultiplier;
        });
      case CELL_TYPES.SELECT_LIST:
        return newRows.sort((a, b) => {
          const parsedA = parser(a[dataColumnID]);
          const parsedB = parser(b[dataColumnID]);

          const dataA =
            isTrimmedStringNonEmpty(parsedA) && parsedA.trim().toLowerCase();
          const dataB =
            isTrimmedStringNonEmpty(parsedB) && parsedB.trim().toLowerCase();

          if (dataA === dataB)
            return (a.originalIndex - b.originalIndex) * directionMultiplier;

          if (!dataA) return 1;
          if (!dataB) return -1;

          return dataA.localeCompare(dataB) * directionMultiplier;
        });

      case CELL_TYPES.SIGNUP_ITEMS:
        return newRows.sort((a, b) => {
          const parsedA = parser(a[dataColumnID]);
          const parsedB = parser(b[dataColumnID]);

          return parsedB.localeCompare(parsedA) * directionMultiplier;
        });

      // TODO implement Address sorting
      // case CELL_TYPES.ADDRESS:
      //   ...
      default:
        return newRows;
    }

    // Aug 2021 → we’re doing a clean slate of all eslint warnings- this is disabled because we don't have time to look at it.
    // eslint-disable-next-line

    // TODO a nicer way to reconcile filters without the unused forceRefresh dependency
  }, [columnID, columnType, direction, rows, forceRefresh]);
}
